'use client';

import Logo from '@/assets/logo.svg';
import { ChevronDown, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useId, useState } from 'react';

const ALGOTEST_HOME = 'https://algotest.in/?utm_source=openbroker&utm_medium=external&utm_campaign=seo&utm_content=product&utm_term=broker-api';
const LOGIN_HREF = 'https://algotest.in/login?utm_source=openbroker&utm_medium=external&utm_campaign=seo&utm_content=product&utm_term=broker-api';
const REGISTER_HREF =
    'https://algotest.in/register?utm_source=openbroker&utm_medium=external&utm_campaign=seo&utm_content=product&utm_term=broker-api';
const BLOG_HREF =
    'https://algotest.in/blog/category/broker?utm_source=openbroker&utm_medium=external&utm_campaign=seo&utm_content=product&utm_term=blog';

const OTHER_TOOLS = [
    {
        label: 'Compare Brokers',
        href: 'https://algotest.in/tools/compare-broker?utm_source=openbroker&utm_medium=external&utm_campaign=seo&utm_content=product&utm_term=brokers',
    },
    {
        label: 'FnO Stocks',
        href: 'https://algotest.in/stocks?utm_source=openbroker&utm_medium=external&utm_campaign=seo&utm_content=product&utm_term=stocks',
    },
];

function HamburgerIcon() {
    return (
        <svg
            width={24}
            height={24}
            viewBox='0 0 24.064 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='block size-6 shrink-0'
            aria-hidden='true'>
            <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M2.4064 5.7C2.4064 5.20294 2.81042 4.8 3.3088 4.8H20.7552C21.2536 4.8 21.6576 5.20294 21.6576 5.7C21.6576 6.19706 21.2536 6.6 20.7552 6.6H3.3088C2.81042 6.6 2.4064 6.19706 2.4064 5.7ZM2.4064 12C2.4064 11.5029 2.81042 11.1 3.3088 11.1H20.7552C21.2536 11.1 21.6576 11.5029 21.6576 12C21.6576 12.4971 21.2536 12.9 20.7552 12.9H3.3088C2.81042 12.9 2.4064 12.4971 2.4064 12ZM2.4064 18.3C2.4064 17.8029 2.81042 17.4 3.3088 17.4H20.7552C21.2536 17.4 21.6576 17.8029 21.6576 18.3C21.6576 18.7971 21.2536 19.2 20.7552 19.2H3.3088C2.81042 19.2 2.4064 18.7971 2.4064 18.3Z'
                fill='#3A3A3A'
            />
        </svg>
    );
}

const OtherTools = () => {
    return (
        <div className='group relative'>
            <button type='button' className='flex items-center gap-1 text-sm' aria-haspopup='true'>
                Other Tools
                <ChevronDown className='size-4 transition-transform group-focus-within:rotate-180 group-hover:rotate-180' />
            </button>
            <div className='invisible absolute right-0 top-full pt-3 opacity-0 transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100'>
                <div className='flex min-w-max flex-col rounded-sm border border-gray-200 bg-white py-1 shadow-lg'>
                    {OTHER_TOOLS.map((tool) => (
                        <Link key={tool.href} href={tool.href} target='_blank' className='px-4 py-2 text-sm hover:bg-gray-100'>
                            {tool.label}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuId = useId();

    useEffect(() => {
        if (!menuOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMenuOpen(() => false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    return (
        <header className='sticky top-0 z-40 border-b border-slate-200 bg-slate-50'>
            <div className='flex items-center justify-between px-4 py-4 md:flex-wrap md:px-5 md:py-3 lg:px-10'>
                <div className='flex items-center gap-2 md:gap-4'>
                    <button
                        type='button'
                        className='inline-flex size-6 shrink-0 items-center justify-center md:hidden'
                        aria-label='Open menu'
                        aria-expanded={menuOpen}
                        aria-controls={menuId}
                        onClick={() => setMenuOpen(true)}>
                        <HamburgerIcon />
                    </button>
                    <div className='flex flex-col items-end md:flex-row md:items-center md:gap-4'>
                        <span className='text-lg font-bold leading-[18px] text-[#2A2A2A] md:text-2xl md:leading-normal'>openbroker.in</span>
                        <div className='flex items-center gap-1 text-[8px] leading-3 text-[#808080] md:gap-2 md:text-base md:leading-normal md:text-inherit'>
                            by
                            <Link href={ALGOTEST_HOME} target='_blank' className='flex items-center gap-1 md:gap-2'>
                                <Image src={Logo} alt='AlgoTest logo' className='size-3 md:size-6' />
                                <span className='font-semibold text-[#3A3A3A] md:font-normal md:text-inherit'>AlgoTest</span>
                            </Link>
                        </div>
                    </div>
                </div>

                <div className='flex items-center gap-2 md:gap-10'>
                    <div className='hidden items-center gap-8 text-sm md:flex'>
                        <OtherTools />
                        <Link href={BLOG_HREF} target='_blank'>
                            Blog
                        </Link>
                    </div>
                    <div className='flex items-center gap-2 md:gap-5'>
                        <Link
                            href={LOGIN_HREF}
                            target='_blank'
                            className='inline-flex h-7 items-center justify-center rounded-[3px] border border-[#0085FF] bg-white px-2.5 text-xs font-medium text-[#0085FF] md:h-auto md:w-max md:rounded md:!py-1.5 md:px-4 md:font-normal'>
                            Login
                        </Link>
                        <Link
                            href={REGISTER_HREF}
                            target='_blank'
                            className='inline-flex h-7 items-center justify-center rounded-[3px] border border-[#0085FF] bg-[#0782DA] px-2.5 text-xs font-medium text-white md:h-auto md:w-max md:rounded md:border-0 md:!py-1.5 md:px-4 md:font-normal'>
                            Sign Up
                        </Link>
                    </div>
                </div>
            </div>

            {menuOpen && (
                <div className='fixed inset-0 z-50 md:hidden'>
                    <button type='button' className='absolute inset-0 bg-black/40' aria-label='Close menu' onClick={() => setMenuOpen(false)} />
                    <aside id={menuId} aria-label='Menu' className='absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col bg-white shadow-xl'>
                        <div className='flex items-center justify-between border-b border-[#F3F3F3] px-4 py-3'>
                            <p className='text-sm font-semibold text-[#2A2A2A]'>Menu</p>
                            <button
                                type='button'
                                className='inline-flex size-8 items-center justify-center rounded-md text-[#3A3A3A]'
                                aria-label='Close menu'
                                onClick={() => setMenuOpen(false)}>
                                <X size={20} aria-hidden='true' />
                            </button>
                        </div>
                        <div className='flex flex-col overflow-y-auto py-2'>
                            <p className='px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#808080]'>Other Tools</p>
                            {OTHER_TOOLS.map((tool) => (
                                <Link
                                    key={tool.href}
                                    href={tool.href}
                                    target='_blank'
                                    className='px-4 py-3 text-sm text-[#2A2A2A] hover:bg-slate-50'
                                    onClick={() => setMenuOpen(false)}>
                                    {tool.label}
                                </Link>
                            ))}
                            <div className='my-2 h-px bg-[#F3F3F3]' />
                            <Link
                                href={BLOG_HREF}
                                target='_blank'
                                className='px-4 py-3 text-sm text-[#2A2A2A] hover:bg-slate-50'
                                onClick={() => setMenuOpen(false)}>
                                Blog
                            </Link>
                            <div className='my-2 h-px bg-[#F3F3F3]' />
                            <Link href={LOGIN_HREF} target='_blank' className='px-4 py-3 text-sm text-[#0085FF]' onClick={() => setMenuOpen(false)}>
                                Login
                            </Link>
                            <Link
                                href={REGISTER_HREF}
                                target='_blank'
                                className='px-4 py-3 text-sm font-medium text-[#0782DA]'
                                onClick={() => setMenuOpen(false)}>
                                Sign Up
                            </Link>
                        </div>
                    </aside>
                </div>
            )}
        </header>
    );
}

export default Header;
